// Полёты наград (ux/13 §6.3 «влёт карточек»): монеты и предметы летят по дуге в шапку или в сумку, цель
// вздрагивает. Слой полёта — поверх всего, без нажатий. В «Меньше движения» полёта нет — только толчок цели (ux/12 §8).
import { animate, bump, DUR, EASE, reduced } from './motion.js';

type Pt = { x: number; y: number };
const center = (r: DOMRect): Pt => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });

/** Пилюли шапки по валюте (kit.ResourceHeader). */
const PILL: Record<string, string> = { nuts: 'res-nuts', talons: 'res-talons', energy: 'res-energy' };
/** «Сумка» для предметов: аватар героя на главном, иначе вкладка «Гараж». */
const BAG = '[data-testid="home-char"], [data-testid="UI-NAV-HOME"]';

function visible(el: Element | null): el is HTMLElement {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight;
}

/**
 * Полёт count копий значка icon из точки from в цель: дуга вверх, сжатие к концу, ступеньки по 55 мс.
 * Промис — когда долетела последняя копия (цель уже толкнули).
 */
export function flyTo(from: Element | DOMRect, target: Element | null, icon: Element | null, count = 5): Promise<void> {
  if (!visible(target)) return Promise.resolve();
  if (reduced() || !icon || typeof document === 'undefined') { bump(target); return Promise.resolve(); }
  const a = center(from instanceof Element ? from.getBoundingClientRect() : from);
  const ti = target.querySelector('svg, img, .ico') ?? target;
  const b = center(ti.getBoundingClientRect());
  const layer = document.createElement('div');
  layer.className = 'fx-layer';
  document.body.appendChild(layer);
  const ir = icon.getBoundingClientRect();
  const done: Promise<void>[] = [];
  for (let i = 0; i < count; i++) {
    const el = icon.cloneNode(true) as HTMLElement;
    el.classList.add('fx-fly');
    el.style.width = `${ir.width || 24}px`;
    el.style.height = `${ir.height || 24}px`;
    el.style.left = `${a.x - (ir.width || 24) / 2}px`;
    el.style.top = `${a.y - (ir.height || 24) / 2}px`;
    layer.appendChild(el);
    // Разлёт в стороны, потом дуга к цели: кадры по квадратичной кривой Безье.
    const spread = { x: (Math.random() - 0.5) * 90, y: -30 - Math.random() * 50 };
    const ctrl = { x: (a.x + b.x) / 2 + spread.x, y: Math.min(a.y, b.y) - 80 + spread.y };
    const kf: Keyframe[] = [];
    for (let s = 0; s <= 8; s++) {
      const t = s / 8;
      const x = (1 - t) ** 2 * a.x + 2 * (1 - t) * t * ctrl.x + t * t * b.x - a.x;
      const y = (1 - t) ** 2 * a.y + 2 * (1 - t) * t * ctrl.y + t * t * b.y - a.y;
      const sc = t < 0.15 ? 0.4 + (t / 0.15) * 0.9 : 1.3 - t * 0.7;
      kf.push({ transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${sc.toFixed(2)})`, opacity: t > 0.92 ? 0.6 : 1, offset: t });
    }
    const an = animate(el, kf, { duration: 620 + Math.random() * 120, delay: i * 55, easing: EASE.inOut, fill: 'both' });
    done.push(an ? an.finished.then(() => { el.remove(); if (i % 2 === 0 || i === count - 1) bump(target, 1.16); }, () => el.remove()) : Promise.resolve());
  }
  return Promise.all(done).then(() => { layer.remove(); });
}

/** Начисленные валюты — монеты в свои пилюли шапки (сколько монет — по размеру суммы, 3–8). */
export function flyGrant(from: Element | DOMRect | null, currencies: Record<string, number> | undefined): void {
  if (!from || !currencies) return;
  for (const [cur, amount] of Object.entries(currencies)) {
    if (!amount || amount < 0 || !PILL[cur]) continue;
    const pill = document.querySelector(`[data-testid="${PILL[cur]}"]`);
    const icon = pill?.querySelector('svg, img, .ico') ?? null;
    void flyTo(from, pill, icon, Math.max(3, Math.min(8, Math.round(2 + Math.log10(amount + 1) * 2))));
  }
}

/** Предметы — в «сумку» (аватар на главном или вкладка «Гараж»): летят сами плитки-значки. */
export function flyToBag(icons: Iterable<Element>): void {
  const bag = [...document.querySelectorAll(BAG)].find(visible) ?? null;
  let i = 0;
  for (const ic of icons) {
    const from = ic.getBoundingClientRect();
    setTimeout(() => { void flyTo(from, bag, ic, 1); }, i++ * 70);
  }
}

/** Вспышка по редкости в точке (ящик): круг света, цвет — переменная --r редкости. */
export function burstAt(host: HTMLElement, at: Pt, rarity: string | null): void {
  if (reduced()) return;
  const el = document.createElement('i');
  el.className = `fx-burst ${rarity ? `rarity-${rarity}` : ''}`;
  const hr = host.getBoundingClientRect();
  el.style.left = `${at.x - hr.left}px`;
  el.style.top = `${at.y - hr.top}px`;
  host.prepend(el); // под плитками выпавшего
  const a = animate(el, [{ opacity: 0, transform: 'translate(-50%, -50%) scale(.2)' }, { opacity: 1, transform: 'translate(-50%, -50%) scale(1)', offset: 0.3 }, { opacity: 0, transform: 'translate(-50%, -50%) scale(1.7)' }], { duration: DUR.scene, easing: EASE.out, fill: 'forwards' });
  const rm = () => el.remove();
  if (a) a.finished.then(rm, rm); else rm();
}

/**
 * Новая стадия зоны гаража (ux/13 §6.3, 1.2 с): облако пыли от основания, вспышка, новая картинка поднимается
 * пружиной. Метка зоны стоит на позиционном transform — двигаем свойством scale и картинку внутри.
 */
export function stageUp(el: HTMLElement): void {
  if (reduced()) { animate(el, [{ opacity: 0.4 }, { opacity: 1 }], { duration: DUR.slow }); return; }
  const host = el.parentElement;
  if (!host) return;
  const r = el.getBoundingClientRect();
  const hr = host.getBoundingClientRect();
  const cx = r.left + r.width / 2 - hr.left;
  const by = r.bottom - hr.top;
  const made: HTMLElement[] = [];
  for (let i = 0; i < 9; i++) {
    const d = document.createElement('i');
    d.className = 'fx-dust';
    d.style.left = `${cx}px`;
    d.style.top = `${by - 8}px`;
    host.appendChild(d);
    made.push(d);
    const a = (i / 9) * Math.PI - Math.PI + (Math.random() - 0.5) * 0.4;
    const dist = 26 + Math.random() * 30;
    animate(d, [
      { opacity: 0, transform: 'translate(-50%, -50%) scale(.4)' },
      { opacity: 0.9, transform: `translate(calc(-50% + ${Math.cos(a) * dist * 0.5}px), calc(-50% + ${Math.sin(a) * dist * 0.4}px)) scale(1)`, offset: 0.25 },
      { opacity: 0, transform: `translate(calc(-50% + ${Math.cos(a) * dist}px), calc(-50% + ${Math.sin(a) * dist * 0.6 - 14}px)) scale(1.8)` },
    ], { duration: 1000 + Math.random() * 200, delay: i * 18, easing: EASE.out, fill: 'both' });
  }
  const flash = document.createElement('i');
  flash.className = 'fx-zone-flash';
  flash.style.left = `${cx}px`;
  flash.style.top = `${r.top + r.height / 2 - hr.top}px`;
  host.appendChild(flash);
  made.push(flash);
  animate(flash, [{ opacity: 0, transform: 'translate(-50%, -50%) scale(.3)' }, { opacity: 0.85, transform: 'translate(-50%, -50%) scale(1)', offset: 0.35 }, { opacity: 0, transform: 'translate(-50%, -50%) scale(1.5)' }], { duration: 650, delay: 220, easing: EASE.out, fill: 'both' });
  const pic = el.querySelector('img') ?? el;
  animate(pic, [{ opacity: 0, transform: 'translateY(18px) scale(.8)' }, { opacity: 0, transform: 'translateY(18px) scale(.8)', offset: 0.3 }, { opacity: 1, transform: 'none' }], { duration: 1100, easing: EASE.spring });
  setTimeout(() => { for (const m of made) m.remove(); }, 1400);
}
